import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { getBrands, createBrand, updateBrand, deleteBrand, uploadFile, getFileUrl } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Plus, Trash, Car, PencilSimple, UploadSimple } from '@phosphor-icons/react';
import { toast } from 'sonner';

const buildLogoSrc = (logoUrl) => {
  if (!logoUrl) return null;
  if (logoUrl.startsWith('http')) return logoUrl;
  // Treat as a file_id and resolve via /api/files/:id
  return getFileUrl(logoUrl);
};

const BrandsPage = () => {
  const { t } = useTranslation();
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBrand, setEditingBrand] = useState(null);
  const [formData, setFormData] = useState({ name: '', logo_url: '' });
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const loadBrands = useCallback(async () => {
    try {
      const res = await getBrands();
      setBrands(res.data);
    } catch (err) {
      console.error('Failed to load brands', err);
      toast.error('Failed to load brands');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadBrands(); }, [loadBrands]);

  const resetForm = () => {
    setEditingBrand(null);
    setFormData({ name: '', logo_url: '' });
  };

  const openCreate = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEdit = (brand) => {
    setEditingBrand(brand);
    setFormData({ name: brand.name || '', logo_url: brand.logo_url || '' });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingBrand) {
        await updateBrand(editingBrand.id, formData);
        toast.success('Brand updated');
      } else {
        await createBrand(formData);
        toast.success('Brand created');
      }
      setDialogOpen(false);
      resetForm();
      loadBrands();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save brand');
    }
  };

  const handleDelete = async (brand) => {
    if (!window.confirm(`Delete brand "${brand.name}"?`)) return;
    try {
      await deleteBrand(brand.id);
      toast.success('Brand deleted');
      loadBrands();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete brand');
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) {
      toast.error('Logo must be under 3 MB');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('category', 'brand_logo');
      const res = await uploadFile(fd);
      setFormData((prev) => ({ ...prev, logo_url: res.data.id }));
      toast.success('Logo uploaded');
    } catch (err) {
      toast.error('Failed to upload logo');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <div data-testid="brands-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t('nav.brands')}</h1>
          <p className="text-muted-foreground text-sm">Manage car brands in your catalog</p>
        </div>
        <Button onClick={openCreate} className="btn-primary" data-testid="add-brand-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Brand
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : brands.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">No brands yet. Add your first car brand!</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {brands.map((brand, idx) => {
            const logoSrc = buildLogoSrc(brand.logo_url);
            return (
              <div key={brand.id} className={`card-brutalist text-center group animate-fade-in stagger-${(idx % 5) + 1}`} data-testid={`brand-card-${brand.id}`}>
                <div className="w-20 h-20 mx-auto mb-3 bg-secondary flex items-center justify-center relative">
                  {logoSrc ? (
                    <img src={logoSrc} alt={brand.name} className="w-16 h-16 object-contain" />
                  ) : (
                    <Car weight="duotone" className="w-10 h-10" />
                  )}
                  <div className="absolute -top-2 -right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => openEdit(brand)}
                      className="w-6 h-6 bg-primary text-white flex items-center justify-center"
                      data-testid={`edit-brand-${brand.id}`}
                      title="Edit"
                    >
                      <PencilSimple className="w-3 h-3" />
                    </button>
                    <button
                      onClick={() => handleDelete(brand)}
                      className="w-6 h-6 bg-destructive text-white flex items-center justify-center"
                      data-testid={`delete-brand-${brand.id}`}
                      title="Delete"
                    >
                      <Trash className="w-3 h-3" />
                    </button>
                  </div>
                </div>
                <h3 className="font-medium text-sm">{brand.name}</h3>
                {brand.supplier_count > 0 && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {brand.supplier_count} supplier{brand.supplier_count > 1 ? 's' : ''}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{editingBrand ? 'Edit Brand' : 'Add Brand'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Brand Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Toyota"
                required
                className="input-brutalist"
                data-testid="brand-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Brand Logo</Label>
              {formData.logo_url && (
                <div className="mb-2 flex items-center gap-3 p-2 border border-border">
                  <img src={buildLogoSrc(formData.logo_url)} alt="Preview" className="w-12 h-12 object-contain bg-secondary" />
                  <button type="button" onClick={() => setFormData({ ...formData, logo_url: '' })} className="text-xs text-destructive hover:underline">Remove</button>
                </div>
              )}
              <div className="flex gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp"
                  onChange={handleLogoUpload}
                  className="hidden"
                  id="brand-logo-input"
                  data-testid="brand-logo-file"
                />
                <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploading} className="w-full">
                  <UploadSimple className="w-4 h-4 mr-2" />
                  {uploading ? 'Uploading...' : (formData.logo_url ? 'Replace Logo' : 'Upload Logo')}
                </Button>
              </div>
              <p className="text-[11px] text-muted-foreground">PNG / JPG / SVG, max 3 MB. Square works best.</p>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>{t('common.cancel')}</Button>
              <Button type="submit" className="btn-primary" data-testid="brand-submit-btn">{t('common.save')}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BrandsPage;
